import { RunContextApp } from "framework/RCApp";
import { CMSApiResponse } from "framework/http/CMSApiClient";
import { ErrorUtils } from "framework/utility/ErrorUtils";
import {
  RunMigration,
  GetMigrationJob,
  ListMigrationJobs,
  MigrationJob
} from "types/apis/migrationInterfaces";
import axios from "axios";

const MIGRATION_TIMEOUT = 120000; // 2 minutes for file upload
// Same CMS base URL the rest of the app uses (see RCApp / CMSApiClient).
const CMS_API_URL = process.env.REACT_APP_CMS_API_URL as string;

export const MigrationApis = {

  /**
   * Run a migration job with CSV/Excel files
   */
  runMigration: async (
    rc: RunContextApp,
    pilotId: string,
    files: {
      locale?: string;
      published?: boolean;
      /** When true, backend runs PARSE + TRANSFORM + VALIDATE only (no import). */
      dryRun?: boolean;
      rowIdDesc?: File;
      boundMatrix?: File;
      ruleMatrix?: File;
      metadata?: File;
      profileInfo?: File;
      defaultProfileInfo?: File;
      otherFiles?: File[];
    }
  ): Promise<CMSApiResponse<RunMigration.retval>> => {

    const formData = new FormData();
    formData.append('pilotId', pilotId);
    if (files.locale != null && files.locale !== '') {
      formData.append('locale', files.locale);
    }
    formData.append('published', files.published === false ? 'false' : 'true');
    if (files.dryRun) {
      formData.append('dryRun', 'true');
    }

    // Field names must match Strapi migration controller + REQUIRED_FILES / OPTIONAL_FILES
    // (row_id_desc, rule_matrix, bound_matrix, metadata, defaultProfileInfo, profileInfo).
    // Extra uploads use unique keys so Koa still exposes each file; detection uses filename patterns.
    if (files.rowIdDesc) formData.append('row_id_desc', files.rowIdDesc);
    if (files.boundMatrix) formData.append('bound_matrix', files.boundMatrix);
    if (files.ruleMatrix) formData.append('rule_matrix', files.ruleMatrix);
    if (files.metadata) formData.append('metadata', files.metadata);
    if (files.profileInfo) formData.append('profileInfo', files.profileInfo);
    if (files.defaultProfileInfo) formData.append('defaultProfileInfo', files.defaultProfileInfo);

    if (files.otherFiles) {
      files.otherFiles.forEach((file, i) => {
        formData.append(`extra_${i}`, file);
      });
    }

    try {
      const response = await axios.post(
        `${CMS_API_URL}/migration/run`,
        formData,
        {
          timeout: MIGRATION_TIMEOUT,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${rc.session.getCMSAccessToken()}`
          }
        }
      );
      return response.data as CMSApiResponse<RunMigration.retval>;
    } catch (error: any) {
      rc.error(rc.getName(MigrationApis), 'Migration run failed', error);
      return {
        error: ErrorUtils.getApiErrorInfo(error)
      };
    }
  },

  /**
   * Get status of a specific migration job
   */
  getJob: async (
    rc: RunContextApp, 
    jobId: string
  ): Promise<CMSApiResponse<GetMigrationJob.retval>> => {
    const resp = await rc.cmsApiClient.get<GetMigrationJob.retval>(
      `/migration/jobs/${jobId}`
    );
    return resp;
  },

  /**
   * List all migration jobs for a pilot
   * Note: Strapi REST API returns { data: [...], meta: {...} }
   * CMSApiClient.get() returns `response.data` (axios) AS `CMSApiResponse<T>`
   * So if Strapi returns { data: [...], meta: {...} }, then:
   *   - resp (CMSApiResponse) = { data: [...], meta: {...} }  (treated as { data?: T, error?: any })
   *   - resp.data = [...] (the array!)
   */
  listJobs: async (
    rc: RunContextApp,
    pilotId: string
  ): Promise<CMSApiResponse<ListMigrationJobs.retval>> => {
    // Select only essential fields for listing - exclude large 'jobs' JSON to avoid MySQL sort memory issues
    const fields = 'fields[0]=jobId&fields[1]=pilotId&fields[2]=jobType&fields[3]=jobStatus&fields[4]=progress&fields[5]=createdAt';
    const resp = await rc.cmsApiClient.get<MigrationJob[]>(
      `/job-manifests?${fields}&filters[jobType][$eq]=MIGRATION&filters[pilotId][$eq]=${pilotId}&sort=createdAt:desc`
    );

    // resp.data is directly the array of jobs (Strapi's "data" field)
    if (resp.data) {
      return { data: resp.data };
    }
    return { error: resp.error };
  }
};

